import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { switchOrganization, OrganizationAuthorizationRequiredError } from '@/shared/api/auth';
import { track } from '@/shared/analytics/track';
import { useAuth } from '@/shared/hooks/useAuth';
import { useBrandStore } from '@/shared/stores/useBrandStore';
import { APP_ROUTES } from '@/shared/routes/paths';

export function useOrganizationSwitch() {
  const { user, login } = useAuth();
  const setBrandId = useBrandStore((state) => state.setBrandId);
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [switchingToOrgName, setSwitchingToOrgName] = useState('');

  useEffect(() => {
    return () => {
      setIsLoading(false);
      setSwitchingToOrgName('');
    };
  }, []);

  const organizations = user?.organizations ?? [];
  const currentOrganization = organizations.find(
    (org) => org.id === user?.organization?.id
  );

  const handleSwitch = async (orgId: string, orgName: string) => {
    const fromOrgId = user?.organization?.id ?? null;
    if (orgId === fromOrgId) {
      return;
    }

    try {
      setIsLoading(true);
      setSwitchingToOrgName(orgName);
      const newUserData = await switchOrganization(orgId);
      setBrandId(null);
      login(newUserData);
      track('organization_switched', {
        from_org_id: fromOrgId,
        to_org_id: orgId,
        to_org_name: orgName,
      });
      if (location.pathname === APP_ROUTES.NOT_FOUND) {
        navigate(APP_ROUTES.ROOT, { replace: true });
      }
      setIsLoading(false);
      setSwitchingToOrgName('');
    } catch (error) {
      if (error instanceof OrganizationAuthorizationRequiredError) {
        setSwitchingToOrgName('Session expired, redirecting to login');
        return;
      }

      setIsLoading(false);
      setSwitchingToOrgName('');
    }
  };

  return {
    organizations,
    currentOrganization,
    isLoading,
    switchingToOrgName,
    handleSwitch,
  };
}
